import { useEffect, useState } from 'react';

const STATUS_OPTIONS = ['active', 'paused', 'completed', 'planned'];
const COLUMN_OPTIONS = ['now', 'next', 'later', 'done'];
const ITEM_STATUSES = ['completed', 'in_progress', 'blocked'];

const ITEM_STATUS_LABELS = {
  completed: 'Completed',
  in_progress: 'In Progress',
  blocked: 'Blocked',
};

const ITEM_STATUS_COLORS = {
  completed: 'bg-accent-green/20 text-accent-green',
  in_progress: 'bg-accent-blue/20 text-accent-blue',
  blocked: 'bg-accent-red/20 text-accent-red',
};

export default function ProjectDrawer({ project, onClose, onUpdate }) {
  // Local mirror so editing is smooth; we push to Supabase on blur / change.
  const [draft, setDraft] = useState(project);
  const [newItemText, setNewItemText] = useState({
    completed: '',
    in_progress: '',
    blocked: '',
  });

  useEffect(() => {
    setDraft(project);
  }, [project?.id]);

  // Keep drawer in sync with realtime updates from elsewhere while open.
  useEffect(() => {
    if (project && draft && project.id === draft.id) {
      // Only override fields not currently focused — naive: full merge.
      // Acceptable for v1 since edits flush on blur immediately.
      setDraft((d) => ({ ...d, ...project }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.last_edited_at]);

  if (!project) return null;

  function save(patch) {
    setDraft((d) => ({ ...d, ...patch }));
    onUpdate(project.id, patch);
  }

  function updateItems(nextItems) {
    save({ items: nextItems });
  }

  const items = Array.isArray(draft.items) ? draft.items : [];

  function addItem(status) {
    const text = newItemText[status].trim();
    if (!text) return;
    updateItems([...items, { text, status }]);
    setNewItemText((prev) => ({ ...prev, [status]: '' }));
  }

  function deleteItem(idx) {
    updateItems(items.filter((_, i) => i !== idx));
  }

  function editItemText(idx, text) {
    const next = items.map((it, i) => (i === idx ? { ...it, text } : it));
    updateItems(next);
  }

  function cycleItemStatus(idx) {
    const cur = items[idx].status;
    const nextStatus =
      ITEM_STATUSES[(ITEM_STATUSES.indexOf(cur) + 1) % ITEM_STATUSES.length];
    const next = items.map((it, i) =>
      i === idx ? { ...it, status: nextStatus } : it
    );
    updateItems(next);
  }

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className="fixed inset-0 bg-black/40 z-40 animate-fade-in"
      />

      {/* Panel */}
      <aside className="fixed inset-y-0 right-0 z-50 w-full md:w-[440px] bg-bg-card border-l border-border-base shadow-xl animate-slide-in-right overflow-y-auto">
        <div className="p-5">
          {/* Header row */}
          <div className="flex items-start justify-between gap-3 mb-4">
            <input
              type="text"
              value={draft.name || ''}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              onBlur={(e) => save({ name: e.target.value })}
              className="flex-1 text-lg font-semibold text-text-primary bg-transparent border-0 border-b border-transparent hover:border-border-base focus:border-accent-blue focus:outline-none px-0 py-1"
            />
            <button
              onClick={onClose}
              className="text-text-secondary hover:text-text-primary transition shrink-0"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {/* Summary */}
          <Field label="Summary">
            <input
              type="text"
              value={draft.summary || ''}
              onChange={(e) => setDraft((d) => ({ ...d, summary: e.target.value }))}
              onBlur={(e) => save({ summary: e.target.value })}
              placeholder="One-line description…"
              className="w-full text-sm bg-bg-input border border-border-base rounded px-2 py-1.5 text-text-primary focus:outline-none focus:border-accent-blue"
            />
          </Field>

          {/* Row: status + column + progress */}
          <div className="grid grid-cols-3 gap-2">
            <Field label="Status">
              <select
                value={draft.status || 'active'}
                onChange={(e) => save({ status: e.target.value })}
                className="w-full text-sm bg-bg-input border border-border-base rounded px-2 py-1.5 text-text-primary focus:outline-none focus:border-accent-blue"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Column">
              <select
                value={draft.kanban_column || 'later'}
                onChange={(e) => save({ kanban_column: e.target.value })}
                className="w-full text-sm bg-bg-input border border-border-base rounded px-2 py-1.5 text-text-primary focus:outline-none focus:border-accent-blue"
              >
                {COLUMN_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Progress %">
              <input
                type="number"
                min="0"
                max="100"
                value={draft.progress ?? 0}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, progress: Number(e.target.value) }))
                }
                onBlur={(e) => save({ progress: Number(e.target.value) })}
                className="w-full text-sm bg-bg-input border border-border-base rounded px-2 py-1.5 text-text-primary focus:outline-none focus:border-accent-blue"
              />
            </Field>
          </div>

          {/* Needs from Jacob */}
          <Field label="Needs from Jacob">
            <textarea
              value={draft.needs_from_jacob || ''}
              onChange={(e) =>
                setDraft((d) => ({ ...d, needs_from_jacob: e.target.value }))
              }
              onBlur={(e) => save({ needs_from_jacob: e.target.value || null })}
              rows={3}
              placeholder="What's blocking? What decision is needed?"
              className="w-full text-sm bg-bg-input border border-border-base rounded px-2 py-1.5 text-text-primary focus:outline-none focus:border-accent-blue resize-none"
            />
          </Field>

          {/* Items by status */}
          {ITEM_STATUSES.map((status) => {
            const inThis = items
              .map((it, idx) => ({ ...it, idx }))
              .filter((it) => it.status === status);
            return (
              <div key={status} className="mt-5">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs uppercase tracking-wider text-text-secondary">
                    {ITEM_STATUS_LABELS[status]}
                  </h4>
                  <span className="text-[10px] text-text-muted">{inThis.length}</span>
                </div>
                <ul className="space-y-1.5">
                  {inThis.map((it) => (
                    <li
                      key={it.idx}
                      className="group flex items-start gap-2 px-2 py-1.5 bg-bg-input/40 rounded"
                    >
                      <button
                        onClick={() => cycleItemStatus(it.idx)}
                        className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded shrink-0 ${ITEM_STATUS_COLORS[it.status]}`}
                        title="Click to cycle status"
                      >
                        {it.status === 'in_progress' ? 'wip' : it.status.charAt(0)}
                      </button>
                      <input
                        type="text"
                        value={it.text}
                        onChange={(e) => editItemText(it.idx, e.target.value)}
                        className="flex-1 text-sm bg-transparent border-0 text-text-primary focus:outline-none px-0"
                      />
                      <button
                        onClick={() => deleteItem(it.idx)}
                        className="text-text-muted hover:text-accent-red transition opacity-0 group-hover:opacity-100 text-xs"
                        aria-label="Delete item"
                      >
                        ✕
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="flex gap-1.5 mt-1.5">
                  <input
                    type="text"
                    value={newItemText[status]}
                    onChange={(e) =>
                      setNewItemText((p) => ({ ...p, [status]: e.target.value }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') addItem(status);
                    }}
                    placeholder={`+ Add ${status === 'in_progress' ? 'in-progress' : status} item…`}
                    className="flex-1 text-xs bg-bg-input border border-border-base rounded px-2 py-1 text-text-primary focus:outline-none focus:border-accent-blue"
                  />
                  <button
                    onClick={() => addItem(status)}
                    disabled={!newItemText[status].trim()}
                    className="text-xs px-2 bg-accent-blue text-white rounded hover:opacity-90 disabled:opacity-30 transition"
                  >
                    Add
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </aside>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div className="mt-3">
      <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">
        {label}
      </div>
      {children}
    </div>
  );
}
